import { BodyBatteryDay, DailyHealth, HealthResource, HealthRange, HrvDay, SleepNight, WeightDay } from '../api/types';
import { addDaysIso, daysBetween, isoDaysAgo } from './format';

/**
 * Metric definitions for the history pages (/health/:metric). Each metric reads one /health resource and
 * turns a row into named values. The first field is the headline value: it drives the stats and the trend.
 */

export const METRIC_KEYS = [
  'hrv',
  'resting-hr',
  'sleep',
  'body-battery',
  'stress',
  'steps',
  'weight',
  'respiration',
  'spo2',
  'calories',
  'floors',
  'intensity',
  'hydration',
] as const;
export type MetricKey = (typeof METRIC_KEYS)[number];

/** Values of one day. null means Garmin did not return the field for that day. */
export type DayValues = Record<string, number | null>;

export interface MetricField {
  key: string;
  label: string;
  unit?: string;
  digits?: number;
  /** Set when the field is drawn in the chart. */
  plot?: { color: string; dashed?: boolean };
}

export interface MetricDef {
  key: MetricKey;
  title: string;
  description: string;
  resource: HealthResource;
  chart: 'line' | 'bar';
  fields: MetricField[];
  /** Fixed y-axis domain. Other metrics use the data range. */
  domain?: [number | string, number | string];
  /** Maps a raw row from the API to the fields above. */
  // The row type differs per metric (DailyHealth, SleepNight, ...). Each definition below types its own row.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: (row: any) => DayValues;
}

/** A 0 to 1 share as whole percent. */
const share = (value: number | null) => (value == null ? null : Math.round(value * 100));

const hours = (seconds: number | null) => (seconds == null ? null : Number((seconds / 3600).toFixed(2)));

export const METRICS: Record<MetricKey, MetricDef> = {
  hrv: {
    key: 'hrv',
    title: 'HRV',
    description: 'Heart rate variability (ms). The headline value is last night; the dashed line is the 7-day average.',
    resource: 'hrv',
    chart: 'line',
    domain: ['dataMin - 5', 'dataMax + 5'],
    fields: [
      { key: 'lastNight', label: 'Last night', unit: 'ms', digits: 0, plot: { color: 'var(--chart-1)' } },
      { key: 'weekly', label: '7-day avg', unit: 'ms', digits: 0, plot: { color: 'var(--chart-3)', dashed: true } },
      { key: 'balancedLow', label: 'Balanced range, low', unit: 'ms', digits: 0, plot: { color: 'var(--text-muted)', dashed: true } },
      { key: 'balancedUpper', label: 'Balanced range, high', unit: 'ms', digits: 0, plot: { color: 'var(--text-muted)', dashed: true } },
      { key: 'peak5', label: 'Best 5 minutes', unit: 'ms', digits: 0 },
    ],
    values: (row: HrvDay) => ({
      lastNight: row.lastNightAvg,
      weekly: row.weeklyAvg,
      balancedLow: row.baselineBalancedLow,
      balancedUpper: row.baselineBalancedUpper,
      peak5: row.lastNight5MinHigh,
    }),
  },
  'resting-hr': {
    key: 'resting-hr',
    title: 'Resting heart rate',
    description: 'Resting heart rate (bpm), one value per day.',
    resource: 'daily',
    chart: 'line',
    domain: ['dataMin - 3', 'dataMax + 3'],
    fields: [
      { key: 'bpm', label: 'Resting HR', unit: 'bpm', digits: 0, plot: { color: 'var(--chart-4)' } },
      { key: 'min', label: 'Lowest of the day', unit: 'bpm', digits: 0 },
      { key: 'max', label: 'Highest of the day', unit: 'bpm', digits: 0 },
    ],
    values: (row: DailyHealth) => ({ bpm: row.restingHr, min: row.hrMin, max: row.hrMax }),
  },
  sleep: {
    key: 'sleep',
    title: 'Sleep',
    description: 'Total sleep per night (hours) and the Garmin sleep score.',
    resource: 'sleep',
    chart: 'bar',
    domain: [0, 'dataMax + 1'],
    fields: [
      { key: 'hours', label: 'Hours asleep', unit: 'h', digits: 1, plot: { color: 'var(--chart-2)' } },
      { key: 'score', label: 'Sleep score', digits: 0 },
      { key: 'deep', label: 'Deep', unit: 'h', digits: 1 },
      { key: 'rem', label: 'REM', unit: 'h', digits: 1 },
      { key: 'light', label: 'Light', unit: 'h', digits: 1 },
      { key: 'awake', label: 'Awake', unit: 'h', digits: 1 },
      { key: 'awakeCount', label: 'Times awake', digits: 0 },
      { key: 'nap', label: 'Naps', unit: 'h', digits: 1 },
      { key: 'spo2', label: 'Blood oxygen', unit: '%', digits: 0 },
      { key: 'respiration', label: 'Breathing', unit: 'brpm', digits: 1 },
      { key: 'stress', label: 'Stress asleep', digits: 0 },
      { key: 'hr', label: 'Heart rate asleep', unit: 'bpm', digits: 0 },
    ],
    values: (row: SleepNight) => ({
      hours: hours(row.totalSleepS),
      score: row.sleepScore,
      deep: hours(row.deepSleepS),
      rem: hours(row.remSleepS),
      light: hours(row.lightSleepS),
      awake: hours(row.awakeS),
      awakeCount: row.awakeCount,
      nap: hours(row.napS),
      spo2: row.avgSpo2,
      respiration: row.avgRespiration,
      stress: row.avgSleepStress,
      hr: row.avgHeartRate,
    }),
  },
  'body-battery': {
    key: 'body-battery',
    title: 'Body battery',
    description: 'Highest and lowest body battery of each day (0 to 100).',
    resource: 'body-battery',
    chart: 'line',
    domain: [0, 100],
    fields: [
      { key: 'high', label: 'Highest', digits: 0, plot: { color: 'var(--chart-1)' } },
      { key: 'low', label: 'Lowest', digits: 0, plot: { color: 'var(--chart-2)' } },
      { key: 'charged', label: 'Charged', digits: 0 },
      { key: 'drained', label: 'Drained', digits: 0 },
    ],
    values: (row: BodyBatteryDay) => ({ high: row.highest, low: row.lowest, charged: row.charged, drained: row.drained }),
  },
  stress: {
    key: 'stress',
    title: 'Stress',
    description: 'Average stress for the day (Garmin scale, 0 to 100).',
    resource: 'daily',
    chart: 'line',
    domain: [0, 100],
    fields: [
      { key: 'avg', label: 'Average stress', digits: 0, plot: { color: 'var(--chart-3)' } },
      { key: 'max', label: 'Highest stress', digits: 0, plot: { color: 'var(--chart-4)', dashed: true } },
      { key: 'rest', label: 'Time resting', unit: '%', digits: 0 },
      { key: 'low', label: 'Time in low stress', unit: '%', digits: 0 },
      { key: 'medium', label: 'Time in medium stress', unit: '%', digits: 0 },
      { key: 'high', label: 'Time in high stress', unit: '%', digits: 0 },
    ],
    values: (row: DailyHealth) => ({
      avg: row.stressAvg,
      max: row.stressMax,
      rest: share(row.stressRestShare),
      low: share(row.stressLowShare),
      medium: share(row.stressMediumShare),
      high: share(row.stressHighShare),
    }),
  },
  steps: {
    key: 'steps',
    title: 'Steps',
    description: 'Steps per day.',
    resource: 'daily',
    chart: 'bar',
    domain: [0, 'auto'],
    fields: [
      { key: 'steps', label: 'Steps', digits: 0, plot: { color: 'var(--chart-1)' } },
      { key: 'goal', label: 'Step goal', digits: 0 },
      { key: 'distance', label: 'Distance walked', unit: 'km', digits: 1 },
    ],
    values: (row: DailyHealth) => ({
      steps: row.steps,
      goal: row.stepGoal,
      distance: row.distanceM == null ? null : Number((row.distanceM / 1000).toFixed(2)),
    }),
  },
  weight: {
    key: 'weight',
    title: 'Weight',
    description: 'Your latest weigh-in of each day (kg), with body composition when the scale reports it.',
    resource: 'weight',
    chart: 'line',
    fields: [
      { key: 'kg', label: 'Weight', unit: 'kg', digits: 1, plot: { color: 'var(--chart-2)' } },
      { key: 'bmi', label: 'BMI', digits: 1 },
      { key: 'fat', label: 'Body fat', unit: '%', digits: 1 },
      { key: 'water', label: 'Body water', unit: '%', digits: 1 },
      { key: 'visceral', label: 'Visceral fat', digits: 0 },
      { key: 'metabolicAge', label: 'Metabolic age', digits: 0 },
    ],
    values: (row: WeightDay) => ({
      kg: row.weightKg,
      bmi: row.bmi,
      fat: row.bodyFatPct,
      water: row.bodyWaterPct,
      visceral: row.visceralFat,
      metabolicAge: row.metabolicAge,
    }),
  },
  respiration: {
    key: 'respiration',
    title: 'Breathing rate',
    description: 'Breaths per minute over the day: the average, and the lowest and highest.',
    resource: 'daily',
    chart: 'line',
    fields: [
      { key: 'avg', label: 'Average', unit: 'brpm', digits: 0, plot: { color: 'var(--chart-1)' } },
      { key: 'min', label: 'Lowest', unit: 'brpm', digits: 0, plot: { color: 'var(--chart-2)', dashed: true } },
      { key: 'max', label: 'Highest', unit: 'brpm', digits: 0, plot: { color: 'var(--chart-4)', dashed: true } },
    ],
    values: (row: DailyHealth) => ({ avg: row.respirationAvg, min: row.respirationMin, max: row.respirationMax }),
  },
  spo2: {
    key: 'spo2',
    title: 'Blood oxygen',
    description: 'Pulse-ox blood oxygen (%): the daily average and the lowest reading.',
    resource: 'daily',
    chart: 'line',
    fields: [
      { key: 'avg', label: 'Average', unit: '%', digits: 0, plot: { color: 'var(--chart-1)' } },
      { key: 'min', label: 'Lowest', unit: '%', digits: 0, plot: { color: 'var(--chart-4)', dashed: true } },
    ],
    values: (row: DailyHealth) => ({ avg: row.spo2Avg, min: row.spo2Min }),
  },
  calories: {
    key: 'calories',
    title: 'Calories',
    description: 'Calories burned per day (kcal), with the active part and the resting part.',
    resource: 'daily',
    chart: 'bar',
    domain: [0, 'auto'],
    fields: [
      { key: 'total', label: 'Total burned', unit: 'kcal', digits: 0, plot: { color: 'var(--chart-3)' } },
      { key: 'active', label: 'Active', unit: 'kcal', digits: 0 },
      { key: 'resting', label: 'Resting', unit: 'kcal', digits: 0 },
    ],
    values: (row: DailyHealth) => ({ total: row.calories, active: row.caloriesActive, resting: row.caloriesResting }),
  },
  floors: {
    key: 'floors',
    title: 'Floors climbed',
    description: 'Floors climbed per day.',
    resource: 'daily',
    chart: 'bar',
    domain: [0, 'auto'],
    fields: [{ key: 'floors', label: 'Floors', digits: 0, plot: { color: 'var(--series-5)' } }],
    values: (row: DailyHealth) => ({ floors: row.floorsAscended }),
  },
  intensity: {
    key: 'intensity',
    title: 'Intensity minutes',
    description: 'Minutes of moderate and vigorous activity per day. Garmin counts a vigorous minute twice towards the goal.',
    resource: 'daily',
    chart: 'bar',
    domain: [0, 'auto'],
    fields: [
      { key: 'moderate', label: 'Moderate', unit: 'min', digits: 0, plot: { color: 'var(--chart-2)' } },
      { key: 'vigorous', label: 'Vigorous', unit: 'min', digits: 0, plot: { color: 'var(--series-6)' } },
      { key: 'total', label: 'Total', unit: 'min', digits: 0 },
    ],
    values: (row: DailyHealth) => ({
      moderate: row.moderateMinutes,
      vigorous: row.vigorousMinutes,
      total: row.intensityMinutes,
    }),
  },
  hydration: {
    key: 'hydration',
    title: 'Hydration',
    description: 'Water logged per day (ml) against your goal.',
    resource: 'daily',
    chart: 'bar',
    domain: [0, 'auto'],
    fields: [
      { key: 'ml', label: 'Drunk', unit: 'ml', digits: 0, plot: { color: 'var(--chart-2)' } },
      { key: 'goal', label: 'Goal', unit: 'ml', digits: 0 },
    ],
    values: (row: DailyHealth) => ({ ml: row.hydrationMl, goal: row.hydrationGoalMl }),
  },
};

export function isMetricKey(value: string): value is MetricKey {
  return (METRIC_KEYS as readonly string[]).includes(value);
}

/** Link to a metric's history, optionally for a given range (the Health page passes its own range). */
export function historyPath(key: MetricKey, range?: HealthRange): string {
  return range ? `/health/${key}?from=${range.from}&to=${range.to}` : `/health/${key}`;
}

// ---- ranges ------------------------------------------------------------------

/** Presets offered by the range selector. days is inclusive of today. */
export const RANGE_PRESETS = [
  { key: '14', label: '14 days', days: 14 },
  { key: '30', label: '30 days', days: 30 },
  { key: '90', label: '90 days', days: 90 },
  { key: '182', label: '6 months', days: 182 },
  { key: '365', label: '1 year', days: 365 },
] as const;

/** The backend refuses ranges longer than this (inclusive days). */
export const MAX_RANGE_DAYS = 366;

export const DEFAULT_PRESET = '30';

export interface Period {
  from: string;
  to: string;
  /** Inclusive day count. */
  days: number;
  /** The equal-length period before this one, used for the trend. */
  previous: HealthRange;
  /** Preset key, or 'custom'. */
  preset: string;
}

export function periodFor(from: string, to: string, preset: string): Period {
  const days = daysBetween(from, to) + 1;
  return {
    from,
    to,
    days,
    previous: { from: addDaysIso(from, -days), to: addDaysIso(from, -1) },
    preset,
  };
}

export function presetPeriod(key: string): Period {
  const preset = RANGE_PRESETS.find(p => p.key === key) ?? RANGE_PRESETS[1];
  return periodFor(isoDaysAgo(preset.days - 1), isoDaysAgo(0), preset.key);
}

/** Checks a custom range. Returns an error message for the user, or null when it is usable. */
export function customRangeError(from: string, to: string, today: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return 'Enter both dates.';
  if (from > to) return 'The start date is after the end date.';
  if (to > today) return 'The end date is in the future.';
  if (daysBetween(from, to) + 1 > MAX_RANGE_DAYS) return `A range can be at most ${MAX_RANGE_DAYS} days.`;
  return null;
}

// ---- values and stats ---------------------------------------------------------

export interface DayPoint {
  date: string;
  values: DayValues;
}

/** Rows from the API (oldest first) to day points for one metric. */
export function toPoints(metric: MetricDef, rows: unknown[]): DayPoint[] {
  return rows.map(row => {
    const r = row as { date: string };
    return { date: r.date, values: metric.values(row) };
  });
}

export interface RangeStats {
  /** Days the API returned for the range. */
  fetched: number;
  /** Days where the headline value is present. */
  withValue: number;
  latest: { date: string; value: number } | null;
  average: number | null;
  min: number | null;
  max: number | null;
}

const round = (value: number, digits: number) => Number(value.toFixed(digits));

export function summarize(points: DayPoint[], field: string, digits = 0): RangeStats {
  const valued = points.filter(p => p.values[field] != null);
  const numbers = valued.map(p => p.values[field] as number);
  const sum = numbers.reduce((total, v) => total + v, 0);
  return {
    fetched: points.length,
    withValue: valued.length,
    latest: valued.length ? { date: valued[valued.length - 1].date, value: valued[valued.length - 1].values[field] as number } : null,
    average: numbers.length ? round(sum / numbers.length, digits) : null,
    min: numbers.length ? Math.min(...numbers) : null,
    max: numbers.length ? Math.max(...numbers) : null,
  };
}

export interface Trend {
  delta: number;
  percent: number | null;
}

/** Change of the period average against the previous period. null when either period has no values. */
export function trendOf(current: number | null, previous: number | null, digits = 0): Trend | null {
  if (current == null || previous == null) return null;
  const delta = round(current - previous, digits);
  return { delta, percent: previous === 0 ? null : Math.round((delta / previous) * 100) };
}
